import dayjs from 'dayjs';
import { BatchExecution } from '../models/batch-execution.model';
import { formatStartedAtLabel } from './batch-executions-chart.util';
import { buildXAxisDataZoom, CHART_GRID_BOTTOM, shouldShowSeriesLabels } from 'src/app/shared/components/charts/chart-zoom.util';

const IMPROVE_COLOR = '#00812B';
const WORSEN_COLOR = '#FF2E1F';

interface DurationDeltaPoint {
  label: string;
  diffPercent: number;
}

function formatSignedPercent(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function buildDeltaPoints(rows: BatchExecution[]): DurationDeltaPoint[] {
  const sortedRows = [...rows].sort(
    (a, b) => dayjs(a.started_at).valueOf() - dayjs(b.started_at).valueOf()
  );

  const points: DurationDeltaPoint[] = [];

  for (let index = 1; index < sortedRows.length; index++) {
    const previousDuration = sortedRows[index - 1].duration;
    const currentDuration = sortedRows[index].duration;
    const diffPercent =
      previousDuration === 0 ? 0 : ((currentDuration - previousDuration) / previousDuration) * 100;

    points.push({
      label: formatStartedAtLabel(sortedRows[index].started_at),
      diffPercent,
    });
  }

  return points;
}

function getPercentAxisBounds(values: number[]): { min: number; max: number; interval: number } {
  if (values.length === 0) {
    return { min: -100, max: 100, interval: 50 };
  }

  const minValue = Math.min(...values, 0);
  const maxValue = Math.max(...values, 0);
  const min = Math.floor(Math.min(minValue - 10, -10) / 50) * 50;
  const max = Math.ceil(Math.max(maxValue + 10, 10) / 50) * 50;
  const interval = Math.max(50, Math.round((max - min) / 6 / 50) * 50);

  return { min, max, interval };
}

export function buildDurationDeltaChartOptions(rows: BatchExecution[]): Record<string, unknown> | null {
  const points = buildDeltaPoints(rows);

  if (points.length === 0) {
    return null;
  }

  const categories = points.map((point) => point.label);
  const percentValues = points.map((point) => point.diffPercent);
  const { min, max, interval } = getPercentAxisBounds(percentValues);
  const showLabels = shouldShowSeriesLabels(categories.length);
  const hasManyPoints = categories.length > 15;

  return {
    grid: {
      left: 56,
      right: 24,
      top: 32,
      bottom: CHART_GRID_BOTTOM,
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const item = params as {
          seriesName: string;
          value: number;
          color: string;
          dataIndex: number;
        };

        const point = points[item.dataIndex];
        if (!point) return '';

        const variationLabel = point.diffPercent >= 0 ? 'Piora' : 'Melhora';

        return `<div style="padding:6px 8px;">
          <div style="color:#575C66;font-size:12px;margin-bottom:4px;">${point.label}</div>
          <div style="color:${item.color};font-size:13px;">${variationLabel}</div>
          <div style="font-size:13px;">
            Variação: <strong>${formatSignedPercent(point.diffPercent)}</strong>
          </div>
        </div>`;
      },
    },
    dataZoom: buildXAxisDataZoom(categories.length),
    legend: {
      show: true,
      left: 0,
      bottom: 0,
      itemGap: 20,
      data: [
        { name: 'Melhora (negativo)', icon: 'rect', itemStyle: { color: IMPROVE_COLOR } },
        { name: 'Piora (positivo)', icon: 'rect', itemStyle: { color: WORSEN_COLOR } },
      ],
      textStyle: {
        color: '#1C2633',
        fontSize: 12,
      },
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: { show: true, lineStyle: { color: '#C6C8CC' } },
      axisTick: { alignWithLabel: true },
      axisLabel: {
        rotate: 45,
        color: '#575C66',
        fontSize: 9,
        margin: 12,
        hideOverlap: true,
        interval: hasManyPoints ? 'auto' : 0,
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'Variação %',
      min,
      max,
      interval,
      nameTextStyle: { color: '#575C66', fontSize: 12 },
      axisLine: { show: true, lineStyle: { color: '#C6C8CC' } },
      axisLabel: {
        color: '#575C66',
        formatter: (value: number) => `${value > 0 ? '+' : ''}${value}%`,
      },
      splitLine: {
        show: true,
        lineStyle: { color: '#E0E2E5' },
      },
    },
    series: [
      {
        name: 'Variação',
        type: 'bar',
        barMaxWidth: hasManyPoints ? 24 : 48,
        data: points.map((point) => ({
          value: point.diffPercent,
          itemStyle: {
            color: point.diffPercent >= 0 ? WORSEN_COLOR : IMPROVE_COLOR,
          },
        })),
        label: {
          show: showLabels,
          position: (params: { value: number }) => (params.value >= 0 ? 'bottom' : 'top'),
          distance: 4,
          fontSize: 10,
          formatter: (params: { dataIndex: number }) => {
            const point = points[params.dataIndex];
            if (!point) return '';
            return formatSignedPercent(point.diffPercent);
          },
          color: (params: { dataIndex: number }) =>
            points[params.dataIndex]?.diffPercent >= 0 ? WORSEN_COLOR : IMPROVE_COLOR,
        },
      },
    ],
  };
}
